import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { TelegramLoginData } from '@etap/shared';
import { YandexOAuthLogin } from '../../components/YandexOAuthLogin';
import { TelegramLoginWidget } from '../../components/TelegramLoginWidget';
import { LanguageSwitcher } from '../../components/LanguageSwitcher';
import { CookieBanner } from '../../components/CookieBanner/CookieBanner';
import { LEGAL_URLS } from '../../lib/legal';
import styles from './Login.module.css';

interface Props {
  onTelegramAuth: (data: TelegramLoginData) => void | Promise<void>;
  error?: string | null;
  loading?: boolean;
}

/**
 * Страница входа для веб-версии (не Telegram WebApp)
 */
export function Login({ onTelegramAuth, error, loading }: Props) {
  const { t } = useTranslation();
  const { botUsername, botLink } = useMemo(() => {
    const link = import.meta.env.VITE_TELEGRAM_BOT_LINK || 'https://t.me/etap_ru_bot';
    const username = link
      .replace(/^https?:\/\//, '')
      .replace('t.me/', '')
      .split('?')[0]
      .replace(/^@/, '');
    return { botUsername: username || 'etap_ru_bot', botLink: link };
  }, []);

  const handleYandexAuthStart = () => {
    const checkInterval = setInterval(() => {
      const token = localStorage.getItem('etap_token');
      if (token) {
        clearInterval(checkInterval);
        window.location.reload();
      }
    }, 500);
    setTimeout(() => clearInterval(checkInterval), 5 * 60 * 1000);
  };

  return (
    <div className={styles.page}>
      <div className={styles.langCorner}>
        <LanguageSwitcher />
      </div>

      <div className={styles.card}>
        <div className={styles.logo}>
          <img className={styles.logoMark} src="/icons/icon-192.png" alt="" />
          <span className={styles.logoName}>ETAP</span>
        </div>

        <h1 className={styles.title}>{t('login.title')}</h1>
        <p className={styles.subtitle}>{t('login.subtitle')}</p>

        <div className={styles.authBlock}>
          <TelegramLoginWidget botUsername={botUsername} onAuth={onTelegramAuth} loading={loading} />
          {import.meta.env.VITE_YANDEX_CLIENT_ID && <>
            <div className={styles.divider}><span>{t('login.or')}</span></div>
            <YandexOAuthLogin onAuthStart={handleYandexAuthStart} />
          </>}
        </div>

        {loading && <p className={styles.loadingHint}>{t('common.loading')}</p>}
        {error && <p className={styles.errorMsg}>{error}</p>}

        <p className={styles.legal}>
          {t('legal.loginNotice')}{' '}
          <a href={LEGAL_URLS.terms} target="_blank" rel="noreferrer" className={styles.hintLink}>{t('legal.loginTerms')}</a>{' '}
          {t('legal.loginConsentPrefix')}{' '}
          <a href={LEGAL_URLS.consent} target="_blank" rel="noreferrer" className={styles.hintLink}>{t('legal.loginConsent')}</a>{' '}
          {t('legal.loginPolicyPrefix')}{' '}
          <a href={LEGAL_URLS.privacy} target="_blank" rel="noreferrer" className={styles.hintLink}>{t('legal.loginPolicy')}</a>.
        </p>

        <div className={styles.footer}>
          <p className={styles.benefits}>{t('login.benefits')}</p>
          <p className={styles.hint}>
            {t('login.miniAppHint')}{' '}
            <a href={botLink} target="_blank" rel="noreferrer" className={styles.hintLink}>
              {t('login.miniAppLink')}
            </a>
          </p>
        </div>
      </div>

      <CookieBanner />
    </div>
  );
}
